import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import IconButton from '@mui/material/IconButton';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import SaveIcon from '@mui/icons-material/Save';
import { Add, BadgeOutlined, Close, Edit, Person as PersonIcon } from '@mui/icons-material';
import { fetchCompanyUsers, editUserFn, addUser } from '../../user/store/user.actions';
import {
  companyUsers,
  companyUsersLoaded,
  companyUsersError,
  addUserError,
} from '../../user/store/user.selectors';
import { selectAllRoles } from '../../../utils/common/store/common.selectors';
import { fetchAllRoles } from '../../../utils/common/store/common.actions';
import { selectAuthUser } from '../../auth/store/auth.selectors';
import AddUserModal from '../../user/components/AddUsers';
import {
  StyledModal,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalContent,
  StyledCloseButton,
  UserListContainer,
  UserListItem,
  StyledFormControl,
  StyledSelect,
  PrimaryButton,
  flexAlignCenterGap,
} from '../../../utils/common/styles/commonStyles';

const UsersModal = ({ open, companyId, onClose }) => {
  const dispatch = useDispatch();
  const users = useSelector(companyUsers);
  const usersLoaded = useSelector(companyUsersLoaded);
  const usersError = useSelector(companyUsersError);
  const roles = useSelector(selectAllRoles);
  const user = useSelector(selectAuthUser);

  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', roleID: '' });
  const [rolesOptions, setRolesOptions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'warning' });

  useEffect(() => {
    if (!open) return;
    dispatch(fetchAllRoles());
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;
    if (companyId) dispatch(fetchCompanyUsers(companyId));
  }, [open, companyId, dispatch]);

  useEffect(() => {
    if (roles) {
      if (user?.rolename === 'Supervisor')
        setRolesOptions(roles.filter((r) => r.roleName === 'User'));
      else if (user?.rolename === 'Manager')
        setRolesOptions(roles.filter((r) => !r.roleName.includes('Rieker')));
      else setRolesOptions(roles);
    }
  }, [roles, user]);

  useEffect(() => {
    if (editingUser && roles && openEditDialog) {
      setEditForm({
        name: editingUser.name || '',
        email: editingUser.email || '',
        roleID: editingUser.roleID || editingUser.roleId || '',
      });
    }
  }, [editingUser, roles, openEditDialog]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        const payload = { ...editingUser, ...editForm };
        if (payload.roleID && payload.roleId === undefined) {
          payload.roleId = payload.roleID;
        } else if (payload.roleId && payload.roleID === undefined) {
          payload.roleID = payload.roleId;
        }
        await dispatch(editUserFn(payload));
        setSnackbar({ open: true, message: 'User updated', severity: 'success' });
      } else {
        const payload = { ...editForm, companyID: companyId };
        if (payload.roleID && payload.roleId === undefined) {
          payload.roleId = payload.roleID;
        }
        await dispatch(addUser(payload));
        setSnackbar({ open: true, message: 'User added', severity: 'success' });
      }
      if (companyId) dispatch(fetchCompanyUsers(companyId));
      setOpenEditDialog(false);
      setEditingUser(null);
      setEditForm({ name: '', email: '', roleID: '' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: editingUser
          ? addUserError || 'Failed to update user'
          : addUserError || 'Failed to add user',
        severity: 'error',
      });
    }
  };

  const listItems = (() => {
    if (!usersLoaded || !companyId) return null;

    let list = [];
    if (Array.isArray(users)) list = users;
    else if (users && users[companyId])
      list = Array.isArray(users[companyId]) ? users[companyId] : [];

    if (list.length === 0) return <Typography sx={{ p: 2 }}>No users</Typography>;

    return (
      <List disablePadding>
        {list.map((u) => (
          <UserListItem key={u.id} sx={{ ...flexAlignCenterGap(2) }}>
            <Avatar
              src={
                u.profilePictureFileRefID ? `/api/files/${u.profilePictureFileRefID}` : undefined
              }
            >
              {u.name?.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>{u.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {u.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {u.role ||
                  roles?.find((r) => r.id === u.roleID || r.id === u.roleId)?.roleName ||
                  'Unknown'}
              </Typography>
            </Box>
            <Box>
              <IconButton
                size="small"
                onClick={() => {
                  setEditingUser(u);
                  setEditForm({ name: '', email: '', roleID: '' });
                  setOpenEditDialog(true);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Box>
          </UserListItem>
        ))}
      </List>
    );
  })();

  return (
    <>
      <StyledModal open={Boolean(open)} onClose={onClose}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>
              <Box sx={{ ...flexAlignCenterGap(1) }}>
                <PersonIcon sx={{ fontSize: 20 }} />
                <Typography component="span">User Management</Typography>
              </Box>
            </ModalTitle>
            <StyledCloseButton size="small" onClick={onClose}>
              <Close />
            </StyledCloseButton>
          </ModalHeader>

          <ModalContent>
            <Box
              sx={{
                ...flexAlignCenterGap(0),
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography component="span">Company Users</Typography>
              </Box>
              <PrimaryButton
                variant="contained"
                onClick={() => setOpenAddUserModal(true)}
                startIcon={<Add />}
              >
                Add User
              </PrimaryButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <UserListContainer>
              {usersLoaded && companyId ? (
                listItems
              ) : usersError ? (
                <Typography sx={{ p: 2 }} color="error">
                  Failed to load users
                </Typography>
              ) : (
                <Typography sx={{ p: 2 }}>Loading...</Typography>
              )}
            </UserListContainer>
          </ModalContent>
        </ModalContainer>
      </StyledModal>

      <AddUserModal
        open={openAddUserModal}
        handleClose={() => {
          setOpenAddUserModal(false);
          if (companyId) dispatch(fetchCompanyUsers(companyId));
        }}
        initialCompanyID={companyId}
      />

      <Dialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setEditingUser(null);
          setEditForm({ name: '', email: '', roleID: '' });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow:
              '0 0 40px rgba(25, 118, 210, 0.2), 0 20px 60px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.3)',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
            backdropFilter: 'blur(4px)',
            animation: 'slideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '@keyframes slideIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(-30px) scale(0.95)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0) scale(1)',
              },
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#2C2C2C', pb: 1.5 }}>
          {editingUser ? 'Edit User' : 'Add User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={editForm.name}
              onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))}
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    borderColor: '#1976d2',
                    boxShadow: '0 4px 16px rgba(25, 118, 210, 0.15)',
                  },
                  '&.Mui-focused': {
                    boxShadow:
                      '0 0 0 4px rgba(25, 118, 210, 0.15), 0 4px 12px rgba(25, 118, 210, 0.25)',
                  },
                },
                '& .MuiInputBase-input': {
                  transition: 'all 0.3s ease',
                },
              }}
            />
            <TextField
              label="Email"
              value={editForm.email}
              onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))}
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    borderColor: '#1976d2',
                    boxShadow: '0 4px 16px rgba(25, 118, 210, 0.15)',
                  },
                  '&.Mui-focused': {
                    boxShadow:
                      '0 0 0 4px rgba(25, 118, 210, 0.15), 0 4px 12px rgba(25, 118, 210, 0.25)',
                  },
                },
                '& .MuiInputBase-input': {
                  transition: 'all 0.3s ease',
                },
              }}
            />
            <StyledFormControl required fullWidth size="small">
              <Typography component="span" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                Role
              </Typography>
              <StyledSelect
                name="roleID"
                value={editForm.roleID}
                onChange={(e) => setEditForm((s) => ({ ...s, roleID: e.target.value }))}
                label="Role"
                startAdornment={
                  <Box sx={{ ml: 1 }}>
                    <BadgeOutlined color="action" fontSize="small" />
                  </Box>
                }
              >
                {rolesOptions?.map((role) => (
                  <MenuItem value={role.id} key={role.id}>
                    {role.roleName}
                  </MenuItem>
                ))}
              </StyledSelect>
            </StyledFormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setEditingUser(null);
              setEditForm({ name: '', email: '', roleID: '' });
            }}
            sx={{
              textTransform: 'none',
              color: '#666666',
              fontWeight: 600,
              borderRadius: '8px',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSaveUser}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              fontWeight: 600,
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                transition: 'left 0.5s ease',
              },
              '&:hover::before': {
                left: '100%',
              },
              '&:hover': {
                boxShadow: '0 8px 20px rgba(25, 118, 210, 0.25)',
                transform: 'translateY(-4px) scale(1.02)',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

UsersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
};

UsersModal.defaultProps = {
  companyId: null,
};

export default UsersModal;
